import React, { useState } from "react";
import { Link } from "react-router-dom";
import CostCalculatorModal from "@/components/marketing/CostCalculatorModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { Button } from "@/components/ui/button";
import { ENTERPRISE_FEATURES as FEATURES } from "@/lib/content";
import {
  ArrowRight, ShieldCheck, GitBranch, Building, Trophy, Megaphone, Scale, Quote, ShieldAlert, CheckCircle2, X, Calculator, Sigma,
} from "lucide-react";

const USE_CASES = [
  { icon: GitBranch, title: "Find the chokepoints", body: "Clients use WYLO's aggregated data to see where workflows actually stall - the handoffs, meetings and tools that generate the most interruptions - and fix them with better resourcing, targeted training, or a reworked process." },
  { icon: Building, title: "De-risk Remote & Hybrid decisions", body: "Before switching work modalities, some clients ran WYLO as a test: where would focus break down, which teams would struggle, what support would be needed? They avoided considerable cost and damage by knowing beforehand." },
  { icon: Trophy, title: "Recognise high-performing teams", body: "Departmental improvements and consistently protected focus become visible. Clients use it to identify and reward teams that are getting it right - evidence, not anecdote." },
  { icon: Megaphone, title: "Give employees their own voice", body: "Enterprise customers can let individuals use their own data to support strong performance reviews and advocate for better packages - or to come forward and request support when they see a negative trend early." },
  { icon: Scale, title: "Revenue integrity for billable work", body: "Consulting and knowledge-work firms, including attorneys, use WYLO to support revenue-integrity claims when invoicing: the time we billed is a faithful reflection of the time actually spent on your matter." },
  { icon: ShieldCheck, title: "Wellbeing without surveillance", body: "Because WYLO measures the cost of interruption rather than the person, HR and People teams get an early-warning signal for overload and burnout that employees are comfortable having on their machines." },
];

function NumbersModal({ open, onOpenChange, onCalculate }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto z-[200] p-0 [&>button.absolute]:hidden" data-testid="numbers-modal">
        <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#E8ECF2] px-5 md:px-6 py-4 flex items-start justify-between gap-3">
          <DialogHeader className="space-y-0">
            <DialogTitle className="font-display text-xl md:text-2xl tracking-tighter text-[#1A1A2E] pr-2">How we get to £500K+ per 100 employees</DialogTitle>
            <DialogDescription className="sr-only">The calculation behind the £500K+ figure</DialogDescription>
          </DialogHeader>
          <DialogClose aria-label="Close" data-testid="numbers-close"
            className="flex-shrink-0 h-10 w-10 rounded-full bg-[#F8F9FC] border border-[#E8ECF2] text-[#1A1A2E] hover:bg-[#E8ECF2] flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-[#2C6BAC]">
            <X size={18} />
          </DialogClose>
        </div>
        <div className="px-5 md:px-6 py-5 text-sm text-[#555568] leading-relaxed space-y-3">
          <p>We start with a fully-loaded employee cost of <strong className="text-[#1A1A2E]">£50,000 per year</strong> (salary plus employer NI, pension and overhead - conservative for knowledge work, given UK median full-time earnings of roughly £37,000 in 2024). Over 220 working days of 7.5 hours, that is about <strong className="text-[#1A1A2E]">£30 per hour</strong>.</p>
          <p>Field research puts full re-engagement after an interruption at ~23 minutes (Mark, Gudith &amp; Klocke, 2008). If just <strong className="text-[#1A1A2E]">two disruptive interruptions a day</strong> each cost that recovery time, that is 46 minutes daily - about 169 hours a year, or <strong className="text-[#1A1A2E]">~£5,100 per person</strong>. Across 100 employees: <strong className="text-[#1A1A2E]">~£510,000</strong>.</p>
          <p>Most teams experience far more than two disruptive interruptions a day, which is why we write "500K+".</p>
          <div className="pt-2 flex flex-wrap gap-2">
            <Button size="sm" className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white" onClick={onCalculate} data-testid="numbers-to-calculator"><Calculator size={14} className="mr-1.5" /> My Numbers</Button>
            <Link to="/resources#research"><Button size="sm" variant="outline" className="border-[#E8ECF2] bg-white">The research <ArrowRight size={14} className="ml-1.5" /></Button></Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Enterprise() {
  const [calcOpen, setCalcOpen] = useState(false);
  const [numbersOpen, setNumbersOpen] = useState(false);
  return (
    <MarketingLayout>
      <CostCalculatorModal open={calcOpen} onOpenChange={setCalcOpen} />
      <NumbersModal open={numbersOpen} onOpenChange={setNumbersOpen} onCalculate={() => { setNumbersOpen(false); setCalcOpen(true); }} />
      <section className="pt-28 pb-16 max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end" data-testid="enterprise-hero">
        <div className="lg:col-span-8">
          <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-4">Using WYLO · Enterprise</p>
          <h1 className="font-display text-5xl lg:text-7xl tracking-tighter text-[#1A1A2E] leading-[0.95]">
            See the whole organisation<br /><span className="text-[#1A3A5C]">without watching anyone.</span>
          </h1>
          <p className="mt-7 text-lg text-[#555568] leading-relaxed max-w-2xl">
            WYLO Momentum and Command give leadership a truthful picture of where focus is won and lost across the business -
            built on aggregated, anonymised signals that employees are happy to have on their machines. That combination is
            why the insight is trusted, and why it changes decisions.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#features" data-testid="enterprise-cta-features">
              <Button className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white h-11 px-6">Features <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </a>
            <a href="#use-cases" data-testid="enterprise-cta-usecases">
              <Button variant="outline" className="border-[#1A3A5C] text-[#1A1A2E] h-11 px-6">Use Cases</Button>
            </a>
          </div>
        </div>
        <div className="lg:col-span-4 rounded-xl border border-[#E8ECF2] bg-white p-6">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#555568] font-semibold">Why it matters</p>
          <p className="mt-3 font-display text-3xl tracking-tight text-[#1A1A2E]">£500K+</p>
          <p className="text-sm text-[#555568] mt-1">estimated off-focus cost per 100 employees per year, at just two disruptive interruptions a day.</p>
          <div className="mt-4 inline-flex rounded-full border border-[#E8ECF2] bg-[#F8F9FC] p-1" data-testid="enterprise-numbers-group">
            <button type="button" onClick={() => setNumbersOpen(true)} data-testid="enterprise-the-numbers"
              className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold text-[#1A3A5C] hover:bg-white transition-colors">
              <Sigma size={13} /> The numbers
            </button>
            <button type="button" onClick={() => setCalcOpen(true)} data-testid="enterprise-my-numbers"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#1A3A5C] px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-[#2C6BAC] transition-colors">
              <Calculator size={13} /> My Numbers
            </button>
          </div>
        </div>
      </section>

      <section id="features" className="scroll-mt-24 bg-white border-y border-[#E8ECF2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-4">Enterprise features</p>
          <h2 className="font-display text-3xl lg:text-4xl tracking-tighter text-[#1A1A2E] mb-10">Built for scale, governance and change.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <div key={i} className="rounded-xl border border-[#E8ECF2] bg-[#F8F9FC] p-6 hover:-translate-y-1 transition-transform" data-testid={`enterprise-feature-${i}`}>
                <div className="h-10 w-10 rounded-md bg-[#1A3A5C] text-white flex items-center justify-center"><f.icon size={18} /></div>
                <h3 className="mt-4 font-display text-lg font-bold text-[#1A1A2E]">{f.title}</h3>
                <p className="mt-2 text-sm text-[#555568] leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="use-cases" className="scroll-mt-24 max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-4">How enterprises use WYLO</p>
        <h2 className="font-display text-3xl lg:text-4xl tracking-tighter text-[#1A1A2E] mb-4">From aggregate signal to better decisions.</h2>
        <p className="text-[#555568] max-w-3xl mb-8 leading-relaxed">
          The pattern is consistent: leadership finally has an honest measure of the cost of interruption, and because it was
          collected without surveillance, everyone in the room trusts it. Here is what clients have done with it so far.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {USE_CASES.map((u, i) => (
            <div key={i} className="rounded-xl border border-[#E8ECF2] bg-white p-7 flex gap-5" data-testid={`enterprise-usecase-${i}`}>
              <div className="h-11 w-11 shrink-0 rounded-md bg-[#2C6BAC]/10 text-[#2C6BAC] flex items-center justify-center"><u.icon size={20} /></div>
              <div>
                <h3 className="font-display text-xl font-bold text-[#1A1A2E]">{u.title}</h3>
                <p className="mt-2 text-sm text-[#555568] leading-relaxed">{u.body}</p>
              </div>
            </div>
          ))}
        </div>
        <Link to="/resources#case-studies" className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2C6BAC] hover:text-[#1A3A5C]" data-testid="enterprise-case-studies-link">
          Read the anonymised case studies <ArrowRight size={14} />
        </Link>
      </section>

      <section className="bg-[#1A3A5C] text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <Quote size={40} className="md:col-span-1 text-[#7DE3B4]" />
          <blockquote className="md:col-span-8 font-display text-2xl lg:text-3xl tracking-tight leading-snug">
            "The time we billed is a good reflection of the time actually spent on your matter."
          </blockquote>
          <p className="md:col-span-3 text-sm text-[#E8ECF2]/80">What revenue integrity sounds like when a firm can back it with WYLO.</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20">
        <ProcurementPack />
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-20 text-center">
        <h2 className="font-display text-3xl lg:text-4xl tracking-tighter text-[#1A1A2E]">Ready to see your organisation clearly?</h2>
        <p className="mt-4 text-[#555568]">Command includes unlimited seats, dedicated onboarding and a named account manager.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/contact"><Button className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white h-11 px-6">Book a conversation</Button></Link>
        </div>
      </section>
    </MarketingLayout>
  );
}

const PACK_CONTENTS = [
  "Security overview: architecture, encryption, hosting and EU data residency",
  "Data Protection Impact Assessment (DPIA) template completed for WYLO",
  "Data-flow diagrams and derived-signal / one-way hashing model",
  "Sub-processor list and Data Processing Agreement (DPA)",
  "EU AI Act position statement and works-council briefing note",
  "Access control, audit-log and incident-response summaries",
];

function ProcurementPack() {
  const [form, setForm] = useState({ email: "", company: "", role: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.company.trim()) return toast.error("Please add your work email and company.");
    setLoading(true);
    try {
      await api.post("/enterprise/procurement-pack", {
        email: form.email.trim(), company: form.company.trim(),
        role: form.role.trim() || null, notes: form.notes.trim() || null,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send your request. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="rounded-2xl border border-[#E8ECF2] bg-white p-8 lg:p-10 grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="procurement-pack">
      <div className="lg:col-span-6">
        <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-3 inline-flex items-center gap-2"><ShieldAlert size={14} /> For procurement & security teams</p>
        <h2 className="font-display text-3xl tracking-tighter text-[#1A1A2E]">Request the Security &amp; DPIA pack.</h2>
        <p className="mt-4 text-sm text-[#555568] leading-relaxed">
          Everything your procurement, security and data-protection reviewers need to assess WYLO, sent by email. Prefer to
          talk first? Write to <a href="mailto:legal@usewylo.com?subject=Security%20%26%20DPIA%20pack" className="text-[#2C6BAC] hover:underline">legal@usewylo.com</a>.
        </p>
        <ul className="mt-5 space-y-2">
          {PACK_CONTENTS.map((c) => (
            <li key={c} className="text-sm text-[#1A1A2E] flex items-start gap-2"><CheckCircle2 size={15} className="text-[#1D9E75] mt-0.5 shrink-0" /> {c}</li>
          ))}
        </ul>
      </div>
      {done ? (
        <div className="lg:col-span-6 flex items-center" data-testid="procurement-pack-done">
          <div className="rounded-xl bg-[#F8F9FC] border border-[#E8ECF2] p-6">
            <p className="font-display text-xl font-bold text-[#1A1A2E]">Request received.</p>
            <p className="mt-2 text-sm text-[#555568]">We'll email the Security &amp; DPIA pack to <span className="font-mono text-[#1A1A2E]">{form.email}</span> within one working day. Reference: {form.company}.</p>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-4 content-start">
          <div>
            <Label htmlFor="pp-email">Work email</Label>
            <Input id="pp-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" className="mt-1" data-testid="procurement-email" />
          </div>
          <div>
            <Label htmlFor="pp-company">Company</Label>
            <Input id="pp-company" required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Company name" className="mt-1" data-testid="procurement-company" />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="pp-role">Your role (optional)</Label>
            <Input id="pp-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="e.g. Head of Procurement, DPO, CISO" className="mt-1" data-testid="procurement-role" />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="pp-notes">Anything specific you need covered? (optional)</Label>
            <Textarea id="pp-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" data-testid="procurement-notes" />
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <Button type="submit" disabled={loading} className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white h-11 px-6" data-testid="procurement-submit">
              {loading ? "Sending…" : "Email me the pack"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
